import React, { memo, useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTabKeys, getTabConfig } from '../config/tabConfigs';
import { parseCurrentUrl, createNavigationHelper } from '../utils/urlManager';
import { calculateBusinessDays } from '../utils/dateFormatting';
import ProjectsTable from './ProjectsTable';
import CompletedProjectsTable from './CompletedProjectsTable';
import SendTab from './ExportAndSend/SendTab';
import ReadyToModelCardView from './ReadyToModelCardView';
import BIsModal from './BIsModal';
import { Button } from './ui';
import { supabase } from '../lib/supabase';
import { DiscrepancyService } from '../services/discrepancyService';

// Cache bust: 1327
const TabbedProjectInterface = memo(({ 
  projects, 
  onUpdateProject, 
  onDeleteProject, 
  onRowClick, 
  loading,
  searchTerm,
  activeTab: propActiveTab,
  onTabChange: propOnTabChange,
  updatingProjects,
  clients,
  employees,
  filteredEmployees,
  urlProjectId: propUrlProjectId
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse URL to get current tab and project ID
  const { tab: urlTab, id: urlProjectId } = parseCurrentUrl(location.pathname);
  
  // Get exportId from URL search params
  const urlParams = new URLSearchParams(location.search);
  const exportId = urlParams.get('exportId');
  
  // Use URL tab if available, otherwise use prop or default
  const activeTab = urlTab || propActiveTab || 'waiting-to-start';
  const projectIdToOpen = urlProjectId || propUrlProjectId;
  
  // Navigation helper
  const navigationHelper = createNavigationHelper(navigate);
  // State for manual sorting
  const [sortBy, setSortBy] = useState('dueDate'); // 'default', 'jobNumber', 'dueDate', 'float'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [showBIsModal, setShowBIsModal] = useState(false);
  const [draftExportsCount, setDraftExportsCount] = useState(0);
  const [discrepanciesByProject, setDiscrepanciesByProject] = useState({});
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true);
  
  // Handle auto-opening project when URL contains projectId
  useEffect(() => {
    if (projectIdToOpen && projects.length > 0) {
      // Find the project by ID
      const projectToOpen = projects.find(p => p.id === projectIdToOpen);
      if (projectToOpen) {
        // Trigger the row click to open the modal
        onRowClick(projectToOpen);
      }
    }
  }, [projectIdToOpen, projects, onRowClick]);

  // Fetch draft exports count on component mount and periodically
  useEffect(() => {
    const fetchDraftExportsCount = async () => {
      try {
        const { data, error } = await supabase
          .from('export_and_send')
          .select('id', { count: 'exact' })
          .eq('status', 'draft');

        if (error) {
          console.error('Error fetching draft exports count:', error);
          return;
        }

        setDraftExportsCount(data?.length || 0);
      } catch (error) {
        console.error('Error fetching draft exports count:', error);
      }
    };

    fetchDraftExportsCount();

    // Set up interval to refresh count periodically (every 30 seconds)
    const interval = setInterval(fetchDraftExportsCount, 30000);

    return () => clearInterval(interval);
  }, []);

  // Filter projects for search first, then by tab
  const searchFilteredProjects = useMemo(() => {
    if (!projects || projects.length === 0) {
      return [];
    }

    if (!searchTerm) {
      return projects;
    }

    // Search across ALL projects regardless of status
    return projects.filter(project => {
      const searchLower = searchTerm.toLowerCase();
      return project.projectName?.toLowerCase().includes(searchLower) ||
        project.jobNumber?.toLowerCase().includes(searchLower) ||
        project.clientName?.toLowerCase().includes(searchLower) ||
        project.contactName?.toLowerCase().includes(searchLower) ||
        project.modelBuilderEmployee?.name?.toLowerCase().includes(searchLower);
    });
  }, [projects, searchTerm]);

  // Filter projects for the active tab and apply default sorting
  const filteredProjects = useMemo(() => {
    const tabConfig = getTabConfig(activeTab);
    
    if (!searchFilteredProjects || searchFilteredProjects.length === 0) {
      return [];
    }

    // Filter by modeling status and active state
    const filtered = searchFilteredProjects.filter(project => {
      // Special handling for Send tab - show projects where Ready to Export is complete
      if (activeTab === 'send') {
        // For now, show projects that have modeling status of "Ready to Export"
        // Later we'll add logic to check if all Ready to Export subitems are complete
        return project.modelingStatus === 'Ready to Export' &&
               (project.modelingState === 'Active' || !project.modelingState);
      }

      // Must match the tab's modeling status
      const matchesStatus = project.modelingStatus === tabConfig.modelingStatus;

      // Special handling for completed tab - show all completed regardless of active state
      if (activeTab === 'completed') {
        return matchesStatus; // Don't filter by active state for completed
      }

      // For Startup tab only - only show projects that have completed setup
      if (activeTab === 'startup') {
        if (!project.projectSetupComplete) {
          return false;
        }
      }

      // For other tabs, must have modeling state = 'Active' or null/undefined
      const isActive = project.modelingState === 'Active' || !project.modelingState;

      return matchesStatus && isActive;
    });

    // Separate soft hold projects from regular projects
    const regularProjects = filtered.filter(p => !p.isSoftHold);
    const softHoldProjects = filtered.filter(p => p.isSoftHold);

    // Define sorting function
    const sortProjects = (projects) => {
      return projects.sort((a, b) => {
        // Manual sorting overrides default
        if (sortBy === 'jobNumber') {
          const aJob = a.jobNumber || '';
          const bJob = b.jobNumber || '';
          const result = aJob.localeCompare(bJob, undefined, { numeric: true });
          return sortDirection === 'asc' ? result : -result;
        } else if (sortBy === 'dueDate') {
          const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          const result = aDate - bDate;
          return sortDirection === 'asc' ? result : -result;
        } else if (sortBy === 'float') {
          // Calculate business days float for both projects
          const aFloat = calculateBusinessDays(
            a.finalProjectedCompletionDate,
            a.dueDate
          ) ?? Infinity; // Projects without dates go to end

          const bFloat = calculateBusinessDays(
            b.finalProjectedCompletionDate,
            b.dueDate
          ) ?? Infinity; // Projects without dates go to end

          const result = aFloat - bFloat;
          return sortDirection === 'asc' ? result : -result;
        }

        // For completed tab, sort by last modified (most recent first) when no manual sort
        if (activeTab === 'completed' && sortBy === 'default') {
          const aModified = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const bModified = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          return bModified - aModified; // Most recent first
        }

        // Default sorting: 1) High priority first, 2) Due date (earliest first)
        const aPriority = a.isHighPriority ? 1 : 0;
        const bPriority = b.isHighPriority ? 1 : 0;
        if (aPriority !== bPriority) {
          return bPriority - aPriority; // High priority first
        }

        // Then sort by due date (earliest first)
        const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aDate - bDate; // Earlier dates first
      });
    };

    // Apply sorting to both groups
    const sortedRegular = sortProjects(regularProjects);
    const sortedSoftHold = sortProjects(softHoldProjects);

    // Combine: regular projects first, then soft hold projects at bottom
    const combined = [...sortedRegular, ...sortedSoftHold];

    // For completed tab, limit to 100 most recent projects
    if (activeTab === 'completed') {
      return combined.slice(0, 100);
    }

    return combined;
  }, [searchFilteredProjects, activeTab, sortBy, sortDirection]);

  // Fetch discrepancies for visible projects
  useEffect(() => {
    const fetchDiscrepancies = async () => {
      if (!filteredProjects || filteredProjects.length === 0) {
        setDiscrepanciesByProject({});
        return;
      }

      try {
        const projectIds = filteredProjects.map(p => p.id);
        console.log('Fetching discrepancies for filtered projects:', projectIds.length, 'projects');
        const discrepancies = await DiscrepancyService.getDiscrepanciesByProjectIds(projectIds);
        console.log('Fetched discrepancies:', discrepancies);
        setDiscrepanciesByProject(discrepancies);
      } catch (error) {
        console.error('Error fetching discrepancies:', error);
      }
    };

    fetchDiscrepancies();
  }, [filteredProjects]);

  // Note: Auto-resize is now handled by the Table component internally
  // No need for custom event dispatching

  // No longer grouping by priority - all projects in single list

  // Get project counts for each tab (using search-filtered projects)
  const tabCounts = useMemo(() => {
    const counts = {};
    
    getTabKeys().forEach(tabKey => {
      const tabConfig = getTabConfig(tabKey);
      
      // Special handling for Send tab - show count of draft exports
      if (tabKey === 'send') {
        counts[tabKey] = draftExportsCount;
      } else {
        const count = searchFilteredProjects.filter(project => {
          const matchesStatus = project.modelingStatus === tabConfig.modelingStatus;

          // Exclude paused projects from badge counts
          if (project.isSoftHold) {
            return false;
          }

          // Special handling for completed tab
          if (tabKey === 'completed') {
            return matchesStatus; // Don't filter by active state
          }

          // For startup tab, only count projects with setup complete
          if (tabKey === 'startup') {
            if (!project.projectSetupComplete) {
              return false;
            }
          }

          // For other tabs, must be active
          const isActive = project.modelingState === 'Active' || !project.modelingState;
          return matchesStatus && isActive;
        }).length;
        counts[tabKey] = count;
      }
    });
    
    return counts;
  }, [searchFilteredProjects, draftExportsCount]);

  const handleTabChange = (tabKey) => {
    // Navigate to the new tab URL
    navigationHelper.goToProjectTab(tabKey);

    // Call prop function if provided (for backward compatibility)
    if (propOnTabChange) {
      propOnTabChange(tabKey);
    }

    // Reset to due date sorting when switching tabs (soonest first)
    setSortBy('dueDate');
    setSortDirection('asc');
  };

  // Handle sorting changes
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, set to ascending
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  
  // Enhanced row click handler that updates URL
  const handleRowClick = (project, type = 'project') => {
    if (type === 'project') {
      // Update URL to include project ID
      navigationHelper.goToProject(activeTab, project.id);
    }
    
    // Call the original row click handler
    if (onRowClick) {
      onRowClick(project, type);
    }
  };

  const TabButton = ({ tabKey, config, count, isActive }) => (
    <button
      type="button"
      onClick={() => handleTabChange(tabKey)}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        padding: '5px 16px',
        background: isActive ? '#3B82F6' : 'transparent',
        border: 'none',
        borderRadius: '7px',
        color: isActive ? '#FFFFFF' : 'rgba(255, 255, 255, 0.65)',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        fontWeight: isActive ? 600 : 500,
        fontSize: '11px',
        flex: '1',
        minWidth: '100px',
        minHeight: '38px',
        boxShadow: isActive ? '0 1px 3px rgba(59, 130, 246, 0.5), 0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
          e.currentTarget.style.background = 'rgba(75, 85, 99, 0.5)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.65)';
          e.currentTarget.style.background = 'transparent';
        }
      }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1px',
        lineHeight: 1.1,
      }}>
        {config.wrappedLabel ? (
          config.wrappedLabel.map((line, index) => (
            <span
              key={index}
              style={{
                fontSize: '11px',
                fontWeight: 'inherit',
                whiteSpace: 'nowrap',
                color: 'inherit',
                textAlign: 'center',
              }}
            >
              {line}
            </span>
          ))
        ) : (
          <span style={{
            fontSize: '11px',
            fontWeight: 'inherit',
            whiteSpace: 'nowrap',
            color: 'inherit',
            textAlign: 'center',
          }}>
            {config.label}
          </span>
        )}
      </div>
      {tabKey !== 'completed' && (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '18px',
          height: '18px',
          padding: '0 4px',
          background: isActive ? 'rgba(255, 255, 255, 0.3)' : 'rgba(75, 85, 99, 0.8)',
          color: '#FFFFFF',
          borderRadius: '9px',
          fontSize: '10px',
          fontWeight: 600,
          lineHeight: 1,
        }}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div style={{
      background: 'var(--dark-bg)',
      borderRadius: 0,
      boxShadow: 'none',
      overflow: 'visible',
      margin: 0,
      width: '100%',
      minWidth: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    }}>
      {/* Tab Navigation */}
      <div style={{
        background: 'var(--dark-bg)',
        padding: '24px 0',
        borderBottom: '1px solid var(--gray-800)',
        position: 'relative',
        width: '100%',
        minWidth: 0,
        margin: 0,
        flexShrink: 0, // Prevent shrinking
      }}>
        <div style={{
          display: 'flex',
          gap: '2px',
          justifyContent: 'stretch',
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(75, 85, 99, 0.5) transparent',
          padding: '2px',
          background: 'rgba(31, 41, 55, 0.5)',
          borderRadius: '10px',
          border: '1px solid rgba(75, 85, 99, 0.3)',
          marginLeft: '0',
          marginRight: '0',
          boxSizing: 'border-box',
        }}>
          {getTabKeys().map(tabKey => {
            const config = getTabConfig(tabKey);
            const count = tabCounts[tabKey] || 0;
            const isActive = activeTab === tabKey;

            return (
              <TabButton
                key={tabKey}
                tabKey={tabKey}
                config={config}
                count={count}
                isActive={isActive}
              />
            );
          })}
        </div>
      </div>

      {/* Toolbar - Fixed at top, doesn't scroll with table */}
      {activeTab !== 'send' && activeTab !== 'completed' && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          background: 'var(--dark-elevated)',
          borderBottom: '1px solid var(--gray-700)',
          flexShrink: 0, // Prevent shrinking
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}>
            <span style={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {getTabConfig(activeTab).label}
            </span>
            {searchTerm && (
              <span style={{
                fontSize: '14px',
                color: 'var(--interactive-primary)',
                background: 'var(--status-info-bg)',
                padding: '4px 12px',
                borderRadius: '20px',
                fontWeight: 500,
                marginLeft: '12px',
              }}>
                Searching across all statuses for: "{searchTerm}"
              </span>
            )}
          </div>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
          }}>
            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
            <button
              onClick={() => setShowBIsModal(true)}
              title="Manage Background Images Status"
              style={{
                padding: '10px 12px',
                fontSize: '13px',
                fontWeight: 500,
                border: '1px solid var(--gray-600)',
                background: 'var(--dark-elevated)',
                color: 'var(--gray-200)',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                minHeight: '38px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--gray-700)';
                e.currentTarget.style.color = 'var(--gray-100)';
                e.currentTarget.style.borderColor = 'var(--gray-500)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--dark-elevated)';
                e.currentTarget.style.color = 'var(--gray-200)';
                e.currentTarget.style.borderColor = 'var(--gray-600)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              🖼️
              <span>BIs</span>
            </button>
          </div>
        </div>
      )}

      {/* Scrollable Table Container */}
      <div style={{
        background: 'var(--dark-bg)',
        minHeight: '400px',
        width: '100%',
        minWidth: 0,
        margin: 0,
        overflowX: 'auto',
        overflowY: 'visible',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(75, 85, 99, 0.5) transparent',
      }}>
        {activeTab === 'send' ? (
          <SendTab
            projects={filteredProjects}
            onUpdateProject={onUpdateProject}
            clients={clients}
            employees={employees}
            exportId={exportId}
          />
        ) : activeTab === 'ready-to-model' ? (
          <ReadyToModelCardView
            onUpdateProject={onUpdateProject}
            projects={projects}
          />
        ) : activeTab === 'completed' ? (
          <CompletedProjectsTable
            projects={filteredProjects}
            activeTab={activeTab}
            tabConfig={getTabConfig(activeTab)}
            onUpdateProject={onUpdateProject}
            onRowClick={handleRowClick}
            loading={loading}
            updatingProjects={updatingProjects}
            clients={clients}
            employees={employees}
            filteredEmployees={filteredEmployees}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            discrepanciesByProject={discrepanciesByProject}
          />
        ) : (
          <ProjectsTable
            projects={filteredProjects}
            activeTab={activeTab}
            tabConfig={getTabConfig(activeTab)}
            onUpdateProject={onUpdateProject}
            onRowClick={handleRowClick}
            loading={loading}
            updatingProjects={updatingProjects}
            clients={clients}
            employees={employees}
            filteredEmployees={filteredEmployees}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            discrepanciesByProject={discrepanciesByProject}
            autoResizeEnabled={autoResizeEnabled}
            onDisableAutoResize={() => setAutoResizeEnabled(false)}
          />
        )}
      </div>

      {/* BIs Modal */}
      <BIsModal 
        isOpen={showBIsModal}
        onClose={() => setShowBIsModal(false)}
      />
    </div>
  );
});

TabbedProjectInterface.displayName = 'TabbedProjectInterface';

export default TabbedProjectInterface;